import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  Trash2,
  Lock,
  LogOut,
  Loader2,
  Crown,
  User as UserIcon,
  Mail,
  Calendar as CalendarIcon,
  FolderOpen,
  X,
  Check,
  Gift,
  Plus,
  Sparkles,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  PrimaryButton,
  SecondaryButton,
  Overline,
  SectionCard,
  TextInput,
  FieldLabel,
} from "@/components/ui-kit";
import { toast } from "sonner";

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AdminUsers() {
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const nav = useNavigate();

  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetUser, setResetUser] = useState(null);
  const [busy, setBusy] = useState("");

  // Reject non-admins right here in the UI (the API also rejects).
  useEffect(() => {
    if (user && user.role !== "admin") nav("/");
  }, [user, nav]);

  async function load() {
    setLoading(true);
    try {
      const [usersRes, grantsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/comp-grants"),
      ]);
      setUsers(usersRes.data.users || []);
      setGrants(grantsRes.data.grants || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function toggleRole(u) {
    const newRole = u.role === "admin" ? "user" : "admin";
    if (u.id === user.id && newRole !== "admin") {
      toast.error(lang === "es" ? "No puedes quitarte tu propio rol de administrador" : "You cannot demote yourself");
      return;
    }
    if (!window.confirm(
      lang === "es"
        ? `¿Cambiar el rol de ${u.email} a ${newRole === "admin" ? "Administrador" : "Usuario"}?`
        : `Change ${u.email}'s role to ${newRole === "admin" ? "Admin" : "User"}?`
    )) return;
    setBusy(u.id);
    try {
      await api.patch(`/admin/users/${u.id}`, { role: newRole });
      toast.success(lang === "es" ? "Rol actualizado" : "Role updated");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function deleteUser(u) {
    if (u.id === user.id) {
      toast.error(lang === "es" ? "No puedes eliminarte a ti mismo" : "You cannot delete yourself");
      return;
    }
    const confirmText = lang === "es"
      ? `Esto borrará a ${u.email}, sus ${u.project_count} proyecto(s) y todos sus links compartidos. Esta acción es irreversible. ¿Continuar?`
      : `This will delete ${u.email}, their ${u.project_count} project(s) and all their share links. Irreversible. Continue?`;
    if (!window.confirm(confirmText)) return;
    setBusy(u.id);
    try {
      const res = await api.delete(`/admin/users/${u.id}`);
      toast.success(
        lang === "es"
          ? `Usuario eliminado (${res.data.deleted_project_count} proyectos borrados)`
          : `User deleted (${res.data.deleted_project_count} projects removed)`
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  // Cycle tier: free → starter → pro → free. subscription_active is set to
  // true whenever the user lands on starter or pro.
  async function cycleTier(u) {
    const currentTier = u.subscription_tier || "free";
    const next = currentTier === "free" ? "pro" : currentTier === "pro" ? "starter" : "free";
    setBusy(u.id);
    try {
      await api.patch(`/admin/users/${u.id}`, {
        subscription_tier: next,
        subscription_active: next !== "free",
      });
      toast.success(
        lang === "es"
          ? `${u.email} → ${next.toUpperCase()}`
          : `${u.email} → ${next.toUpperCase()}`
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E8E6E1] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => nav("/settings")}
            className="inline-flex items-center gap-2 text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {lang === "es" ? "Ajustes" : "Settings"}
          </button>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={logout} className="text-[#5C5B5A] hover:text-[#1A1A1A] p-1" aria-label="logout">
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div>
          <Overline>
            <Shield className="h-3 w-3 inline mr-1" strokeWidth={1.5} />
            {lang === "es" ? "Administración" : "Administration"}
          </Overline>
          <h1
            className="mt-2 text-4xl sm:text-5xl text-[#1A1A1A] tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            data-testid="admin-title"
          >
            {lang === "es" ? "Usuarios" : "Users"}
          </h1>
          <p className="mt-3 text-sm text-[#5C5B5A] max-w-2xl">
            {lang === "es"
              ? "Aquí ves toda la gente con cuenta en Atelier Business. Puedes cambiar su rol (Usuario / Administrador), resetear su contraseña o eliminar su cuenta con todos sus proyectos."
              : "Every Atelier Business account in one place. Toggle their role, reset their password, or delete them with all their projects."}
          </p>
        </div>

        <SectionCard
          title={lang === "es" ? `${users.length} usuario(s)` : `${users.length} user(s)`}
          subtitle={lang === "es" ? "Ordenados por fecha de registro." : "Sorted by registration date."}
        >
          {loading ? (
            <div className="text-sm text-[#5C5B5A] py-6">{lang === "es" ? "Cargando…" : "Loading…"}</div>
          ) : (
            <ul className="divide-y divide-[#F0EFE9] -mx-6" data-testid="admin-users-list">
              {users.map((u) => {
                const isMe = u.id === user?.id;
                const isAdmin = u.role === "admin";
                return (
                  <li
                    key={u.id}
                    className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4"
                    data-testid={`admin-user-row-${u.email}`}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`rounded-full p-2 mt-0.5 shrink-0 ${isAdmin ? "bg-[#B89154]/15 border border-[#B89154]/30" : "bg-[#2563EB]/10 border border-[#2563EB]/20"}`}>
                        {isAdmin ? (
                          <Crown className="h-4 w-4 text-[#B89154]" strokeWidth={1.5} />
                        ) : (
                          <UserIcon className="h-4 w-4 text-[#2563EB]" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm text-[#1A1A1A] truncate font-medium">
                            {u.name || u.email}
                          </div>
                          <span
                            className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border ${
                              isAdmin
                                ? "border-[#B89154]/40 bg-[#B89154]/10 text-[#8a6a37]"
                                : "border-[#2563EB]/30 bg-[#2563EB]/5 text-[#2563EB]"
                            }`}
                          >
                            {isAdmin
                              ? (lang === "es" ? "Administrador" : "Admin")
                              : (lang === "es" ? "Usuario" : "User")}
                          </span>
                          <TierBadge tier={u.subscription_tier} active={u.subscription_active} lang={lang} />
                          {isMe && (
                            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#E8E6E1] bg-[#FBFAF7] text-[#5C5B5A]">
                              {lang === "es" ? "Tú" : "You"}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-xs text-[#8A8886] flex-wrap" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                          <span className="inline-flex items-center gap-1">
                            <Mail className="h-3 w-3" strokeWidth={1.5} />
                            {u.email}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" strokeWidth={1.5} />
                            {formatDate(u.created_at, lang)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <FolderOpen className="h-3 w-3" strokeWidth={1.5} />
                            {u.project_count}{" "}
                            {lang === "es"
                              ? (u.project_count === 1 ? "proyecto" : "proyectos")
                              : (u.project_count === 1 ? "project" : "projects")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end shrink-0">
                      <SecondaryButton
                        onClick={() => cycleTier(u)}
                        disabled={busy === u.id || isAdmin}
                        testId={`cycle-tier-${u.email}`}
                        className="!py-1.5 !px-3 !text-xs"
                        title={lang === "es" ? "Free → Pro → Starter → Free" : "Cycle tier"}
                      >
                        {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        {lang === "es" ? "Plan" : "Plan"}
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => toggleRole(u)}
                        disabled={busy === u.id || (isMe && isAdmin)}
                        testId={`toggle-role-${u.email}`}
                        className="!py-1.5 !px-3 !text-xs"
                      >
                        {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        {isAdmin
                          ? (lang === "es" ? "Quitar admin" : "Demote")
                          : (lang === "es" ? "Hacer admin" : "Promote")}
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => setResetUser(u)}
                        testId={`reset-password-${u.email}`}
                        className="!py-1.5 !px-3 !text-xs"
                      >
                        <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {lang === "es" ? "Contraseña" : "Password"}
                      </SecondaryButton>
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={busy === u.id || isMe}
                        className="inline-flex items-center justify-center p-2 rounded-sm border border-[#E8E6E1] text-[#8A8886] hover:text-[#9E2A2B] hover:border-[#9E2A2B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid={`delete-user-${u.email}`}
                        aria-label="delete"
                      >
                        {busy === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <CompGrantsCard grants={grants} onChange={load} lang={lang} />
      </main>

      {resetUser && (
        <ResetPasswordModal
          target={resetUser}
          onClose={() => setResetUser(null)}
          onDone={() => {
            setResetUser(null);
            load();
          }}
          lang={lang}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({ target, onClose, onDone, lang }) {
  const [password, setPassword] = useState("");
  const [working, setWorking] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(lang === "es" ? "Mínimo 8 caracteres" : "Minimum 8 characters");
      return;
    }
    setWorking(true);
    try {
      await api.post(`/admin/users/${target.id}/reset-password`, { new_password: password });
      toast.success(
        lang === "es"
          ? `Contraseña de ${target.email} actualizada`
          : `Password for ${target.email} updated`
      );
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="reset-password-modal"
    >
      <div
        className="bg-white border border-[#E8E6E1] rounded-sm max-w-md w-full p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <Overline>{lang === "es" ? "Restablecer contraseña" : "Reset password"}</Overline>
            <h3
              className="mt-2 text-xl text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
            >
              {target.name || target.email}
            </h3>
            <p className="text-xs text-[#8A8886] mt-1" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              {target.email}
            </p>
          </div>
          <button onClick={onClose} className="text-[#8A8886] hover:text-[#1A1A1A]" aria-label="close">
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
        <p className="text-sm text-[#5C5B5A] mb-5">
          {lang === "es"
            ? "Introduce la nueva contraseña. Comunícasela al usuario por un canal seguro — no le notificamos por email."
            : "Type the new password. Communicate it to the user via a secure channel — we don't email them."}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <FieldLabel>{lang === "es" ? "Nueva contraseña" : "New password"}</FieldLabel>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-4 py-2.5 outline-none transition-all"
              data-testid="admin-new-password-input"
              required
              minLength={8}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton type="button" onClick={onClose} testId="cancel-reset-btn">
              {lang === "es" ? "Cancelar" : "Cancel"}
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={working} testId="confirm-reset-btn">
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={1.5} />}
              {lang === "es" ? "Cambiar" : "Update"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}


function TierBadge({ tier, active, lang }) {
  const t = (tier || "free").toLowerCase();
  if (t === "pro") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#7C3AED]/40 bg-gradient-to-r from-[#2563EB]/10 to-[#7C3AED]/10 text-[#7C3AED]"
        title={active ? "Active" : "Cancelled"}
      >
        <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
        Pro
      </span>
    );
  }
  if (t === "starter") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#2563EB]/40 bg-[#2563EB]/5 text-[#2563EB]">
        Starter
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#E8E6E1] bg-[#FBFAF7] text-[#8A8886]">
      {lang === "es" ? "Free" : "Free"}
    </span>
  );
}

function CompGrantsCard({ grants, onChange, lang }) {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("pro");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error(lang === "es" ? "Email inválido" : "Invalid email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/comp-grants", { email: email.trim().toLowerCase(), tier, note });
      toast.success(
        lang === "es"
          ? `Acceso de cortesía ${tier.toUpperCase()} concedido a ${email}`
          : `Comp ${tier.toUpperCase()} granted to ${email}`
      );
      setEmail("");
      setNote("");
      await onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(g) {
    if (!window.confirm(
      lang === "es"
        ? `¿Eliminar acceso de cortesía para ${g.email}?`
        : `Delete comp grant for ${g.email}?`
    )) return;
    setDeleting(g.email);
    try {
      await api.delete(`/admin/comp-grants/${encodeURIComponent(g.email)}`);
      toast.success(lang === "es" ? "Eliminado" : "Removed");
      await onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting("");
    }
  }

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <Gift className="h-4 w-4 text-[#7C3AED]" strokeWidth={1.5} />
          {lang === "es" ? "Accesos de cortesía" : "Comp grants"}
        </span>
      }
      subtitle={
        lang === "es"
          ? "Concede acceso Pro/Starter sin pago. Si el email ya está registrado se aplica al instante. Si no, se aplicará automáticamente cuando esa persona se registre."
          : "Grant Pro/Starter access without payment. If the email is already registered, the tier is applied instantly. Otherwise it's auto-applied when they register."
      }
    >
      <form
        onSubmit={submit}
        className="flex flex-col sm:flex-row gap-3 mb-6 pb-6 border-b border-[#F0EFE9]"
        data-testid="comp-grant-form"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={lang === "es" ? "email@ejemplo.com" : "email@example.com"}
          className="flex-1 min-w-0 border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-sm px-3 py-2 outline-none transition-all"
          required
          data-testid="comp-grant-email-input"
        />
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-sm px-3 py-2 outline-none"
          data-testid="comp-grant-tier-select"
        >
          <option value="pro">Pro</option>
          <option value="starter">Starter</option>
        </select>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={lang === "es" ? "Nota (opcional)" : "Note (optional)"}
          className="flex-1 min-w-0 border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-sm px-3 py-2 outline-none"
          data-testid="comp-grant-note-input"
        />
        <PrimaryButton type="submit" disabled={busy} testId="comp-grant-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" strokeWidth={1.5} />}
          {lang === "es" ? "Conceder" : "Grant"}
        </PrimaryButton>
      </form>

      {grants.length === 0 ? (
        <div className="text-sm text-[#8A8886] py-4">
          {lang === "es"
            ? "Aún no has concedido ningún acceso de cortesía."
            : "No comp grants yet."}
        </div>
      ) : (
        <ul className="divide-y divide-[#F0EFE9] -mx-6" data-testid="comp-grants-list">
          {grants.map((g) => {
            const isPending = !g.applied_at;
            return (
              <li
                key={g.id}
                className="px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`comp-grant-row-${g.email}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-[#1A1A1A] font-medium truncate">{g.email}</span>
                    <TierBadge tier={g.tier} active={true} lang={lang} />
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border ${
                        isPending
                          ? "border-[#E5C97A]/50 bg-[#FBF6E6] text-[#8a6a37]"
                          : "border-[#65A05F]/40 bg-[#65A05F]/10 text-[#3F6F3B]"
                      }`}
                    >
                      {isPending
                        ? (lang === "es" ? "Pendiente" : "Pending")
                        : (lang === "es" ? "Aplicado" : "Applied")}
                    </span>
                  </div>
                  {g.note && (
                    <div className="text-xs text-[#8A8886] mt-1">{g.note}</div>
                  )}
                  <div className="text-[10px] text-[#A8A6A0] mt-0.5" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {g.applied_at
                      ? `${lang === "es" ? "Aplicado" : "Applied"}: ${new Date(g.applied_at).toLocaleString(lang === "es" ? "es-ES" : "en-US")}`
                      : `${lang === "es" ? "Creado" : "Created"}: ${new Date(g.created_at).toLocaleString(lang === "es" ? "es-ES" : "en-US")}`}
                  </div>
                </div>
                <button
                  onClick={() => remove(g)}
                  disabled={deleting === g.email}
                  className="inline-flex items-center justify-center p-2 rounded-sm border border-[#E8E6E1] text-[#8A8886] hover:text-[#9E2A2B] hover:border-[#9E2A2B] disabled:opacity-30 transition-colors shrink-0"
                  data-testid={`delete-comp-grant-${g.email}`}
                  aria-label="delete"
                >
                  {deleting === g.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
